import { useState, useCallback, useEffect, useContext, MutableRefObject } from 'react';
import { unstable_useBlocker as useBlocker } from 'react-router-dom';
import { DispatcherEvents, EventDispatcherContext } from '@/context/eventDispatcherContext';
import config from '@/config';

const useRouterPrompt = (when: boolean, pageLock: boolean = false) => {
  const [showPrompt, setShowPrompt] = useState<boolean>(false);
  const [shouldBlock, setShouldBlock] = useState<boolean>(true);
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const dispatcher = useContext(EventDispatcherContext);

  useEffect(() => {
    if (!config.useTutorStreaming) {
      return;
    }

    const onOpen = () => {
      setIsOnline(false);
    };

    const onClose = () => {
      setIsOnline(true);
    };

    dispatcher.addListener(DispatcherEvents.OFFLINE_POPUP_OPEN, onOpen);
    dispatcher.addListener(DispatcherEvents.OFFLINE_POPUP_CLOSE, onClose);

    return () => {
      dispatcher.removeListener(DispatcherEvents.OFFLINE_POPUP_OPEN, onOpen);
      dispatcher.removeListener(DispatcherEvents.OFFLINE_POPUP_CLOSE, onClose);
    };
  }, [dispatcher]);

  // block a user from leaving a page
  useEffect(() => {
    const onbeforeunload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = true;
      return true;
    };

    if (when && shouldBlock && pageLock && isOnline) {
      window.addEventListener('beforeunload', onbeforeunload);

      return () => {
        window.removeEventListener('beforeunload', onbeforeunload);
      };
    }
  }, [when, shouldBlock, pageLock, isOnline]);

  const blockerFunction = useCallback(() => {
    if (when && isOnline) {
      if (shouldBlock) {
        setShowPrompt(true);
      }

      return shouldBlock;
    }

    return false;
  }, [when, isOnline, shouldBlock]);

  const blocker = useBlocker(blockerFunction);

  const handleConfirm = () => {
    setShowPrompt(false);
    setShouldBlock(false);
    blocker.proceed?.();
  };

  const handleCancel = () => {
    setShowPrompt(false);
    setShouldBlock(true);
    blocker.reset?.();
  };

  return { showPrompt, handleConfirm, handleCancel };
};

interface RouterPromptGateProps {
  when: boolean;
  pageLock?: boolean;
  onShowPromptChange: (showPrompt: boolean) => void;
  handlersRef: MutableRefObject<{ handleConfirm: () => void; handleCancel: () => void }>;
}

export const RouterPromptGate = ({
  when,
  pageLock,
  onShowPromptChange,
  handlersRef,
}: RouterPromptGateProps) => {
  const { showPrompt, handleConfirm, handleCancel } = useRouterPrompt(when, pageLock);

  handlersRef.current = { handleConfirm, handleCancel };

  useEffect(() => {
    onShowPromptChange(showPrompt);
  }, [showPrompt, onShowPromptChange]);

  return null;
};

export default useRouterPrompt;
